import { Edit, Search, Trash2, X } from 'lucide-react'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Input } from '../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'

export default function SportsList({
  sports,
  loading,
  searchTerm,
  onSearchChange,
  selectedSportId,
  onSelectSport,
  isAdmin,
  onOpenAddSport: _onOpenAddSport,
  onOpenEditSport,
  onOpenDeleteSport,
  onSetSportActive,
  // Add Sport Modal
  showAddSportModal,
  onCloseAddSportModal,
  sportForm,
  onSportFormChange,
  onSaveSport,
  // Edit Sport Modal
  showEditSportModal,
  onCloseEditSportModal,
  editingSport,
  // Delete Sport Modal
  showDeleteSportModal,
  onCloseDeleteSportModal,
  deletingSport,
  onConfirmDeleteSport,
}) {
  const statusBadge = (isActive) => (
    <Badge variant={isActive ? 'teal' : 'muted'}>{isActive ? 'Active' : 'Disabled'}</Badge>
  )

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Sports</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search sports..."
                className="pl-10 pr-10"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => onSearchChange('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px]">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">Name</th>
                  <th className="text-left py-3 px-4">Status</th>
                  {isAdmin && <th className="text-right py-3 px-4">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={isAdmin ? 3 : 2} className="py-8 text-center text-muted-foreground">
                      Loading sports...
                    </td>
                  </tr>
                ) : sports.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 3 : 2} className="py-8 text-center text-muted-foreground">
                      No sports found
                    </td>
                  </tr>
                ) : (
                  sports.map((sport) => (
                    <tr
                      key={sport.id}
                      className={`border-b hover:bg-muted/50 cursor-pointer ${
                        selectedSportId === sport.id ? 'bg-muted/30' : ''
                      }`}
                      onClick={() => onSelectSport(sport.id)}
                    >
                      <td className="py-3 px-4 font-medium">{sport.name}</td>
                      <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                        {isAdmin ? (
                          <div className="max-w-[140px]">
                            <Select
                              value={sport.is_active ? 'active' : 'inactive'}
                              onValueChange={(value) => onSetSportActive(sport, value === 'active')}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="inactive">Inactive</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          statusBadge(!!sport.is_active)
                        )}
                      </td>
                      {isAdmin && (
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" onClick={() => onOpenEditSport(sport)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => onOpenDeleteSport(sport)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!isAdmin && (
            <p className="text-xs text-muted-foreground mt-4">
              Sports management is restricted to administrators.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Add Sport Modal */}
      {showAddSportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Add Sport</CardTitle>
                <Button variant="ghost" size="icon" onClick={onCloseAddSportModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Name *</label>
                  <Input
                    value={sportForm.name}
                    onChange={(e) => onSportFormChange({ ...sportForm, name: e.target.value })}
                    placeholder="Pickleball"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Description</label>
                  <Input
                    value={sportForm.description}
                    onChange={(e) => onSportFormChange({ ...sportForm, description: e.target.value })}
                    placeholder="Optional description"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="sport-active"
                    type="checkbox"
                    checked={sportForm.is_active}
                    onChange={(e) => onSportFormChange({ ...sportForm, is_active: e.target.checked })}
                  />
                  <label htmlFor="sport-active" className="text-sm">Active</label>
                </div>
              </div>

              <div className="flex gap-2 pt-4 mt-4 border-t">
                <Button variant="outline" onClick={onCloseAddSportModal} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={() => onSaveSport('add')} className="flex-1" disabled={!sportForm.name}>
                  Create
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Sport Modal */}
      {showEditSportModal && editingSport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Edit Sport</CardTitle>
                <Button variant="ghost" size="icon" onClick={onCloseEditSportModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Name *</label>
                  <Input
                    value={sportForm.name}
                    onChange={(e) => onSportFormChange({ ...sportForm, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Description</label>
                  <Input
                    value={sportForm.description}
                    onChange={(e) => onSportFormChange({ ...sportForm, description: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="sport-active-edit"
                    type="checkbox"
                    checked={sportForm.is_active}
                    onChange={(e) => onSportFormChange({ ...sportForm, is_active: e.target.checked })}
                  />
                  <label htmlFor="sport-active-edit" className="text-sm">Active</label>
                </div>
              </div>

              <div className="flex gap-2 pt-4 mt-4 border-t">
                <Button variant="outline" onClick={onCloseEditSportModal} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={() => onSaveSport('edit')} className="flex-1" disabled={!sportForm.name}>
                  Save
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Sport Modal */}
      {showDeleteSportModal && deletingSport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Delete Sport</CardTitle>
                <Button variant="ghost" size="icon" onClick={onCloseDeleteSportModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <span className="font-semibold">{deletingSport.name}</span>? This cannot be undone.
              </p>
              <div className="flex gap-2 pt-4 mt-4 border-t">
                <Button variant="outline" onClick={onCloseDeleteSportModal} className="flex-1">
                  Cancel
                </Button>
                <Button variant="destructive" onClick={onConfirmDeleteSport} className="flex-1">
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  )
}
